import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface UnitsSettings {
	lengthUnit: "mm" | "cm" | "m" | "in" | "ft";
	massUnit: "kg" | "g" | "lb" | "oz";
	lengthAccuracy: number;
	rollPlacement: "square" | "hexagon";
}

interface UnitsState {
	settings: UnitsSettings;
}

const defaultSettings: UnitsSettings = {
	lengthUnit: "cm", // Default to centimeters
	massUnit: "kg",
	lengthAccuracy: 5,
	rollPlacement: "hexagon",
};

// Load initial state from localStorage if available
const loadInitialState = (): UnitsState => {
	if (typeof window !== "undefined") {
		const savedSettings = localStorage.getItem("unitsSettings");
		if (savedSettings) {
			try {
				return { settings: JSON.parse(savedSettings) };
			} catch (error) {
				console.error("Failed to parse saved units settings:", error);
			}
		}
	}
	return { settings: defaultSettings };
};

const unitsSlice = createSlice({
	name: "units",
	initialState: loadInitialState(),
	reducers: {
		updateUnitsSettings: (
			state,
			action: PayloadAction<Partial<UnitsSettings>>,
		) => {
			state.settings = { ...state.settings, ...action.payload };
			// Save to localStorage
			if (typeof window !== "undefined") {
				localStorage.setItem("unitsSettings", JSON.stringify(state.settings));
			}
		},
		loadUnitsSettings: (state) => {
			if (typeof window !== "undefined") {
				const savedSettings = localStorage.getItem("unitsSettings");
				if (savedSettings) {
					try {
						state.settings = JSON.parse(savedSettings);
					} catch (error) {
						console.error("Failed to load units settings:", error);
					}
				}
			}
		},
	},
});

export const { updateUnitsSettings, loadUnitsSettings } = unitsSlice.actions;
export default unitsSlice.reducer;
